import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Quote } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import MethodologyCogs from "@/components/MethodologyCogs";
import CtaBand from "@/components/CtaBand";
import { SERVICES, CASE_STUDIES, TESTIMONIALS, HERO_IMAGE } from "@/lib/content";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const featured = CASE_STUDIES[0];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[92vh] items-center overflow-hidden bg-white" data-testid="home-hero">
        <img src={HERO_IMAGE} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-[0.08] img-duotone lg:hidden" />
        <motion.div style={{ y: bgY }} className="absolute right-0 -top-16 hidden h-[125%] w-[46%] lg:block">
          <img
            src={HERO_IMAGE}
            alt="Safety engineers at a high-hazard industrial facility"
            className="h-full w-full object-cover img-duotone"
            style={{ clipPath: "polygon(14% 0, 100% 0, 100% 100%, 0 100%)", objectPosition: "88% center" }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/10" aria-hidden="true" />
        <div className="absolute -left-32 top-24 h-96 w-96 rounded-full bg-teal-light blur-3xl opacity-60" aria-hidden="true" />
        <div className="container-arms relative py-24">
          <p className="eyebrow mb-6 mask-line"><span style={{ animationDelay: "0.1s" }}>Advanced Risk Management Solutions Ltd</span></p>
          <h1 className="max-w-4xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.06] text-navy">
            <span className="mask-line"><span style={{ animationDelay: "0.25s" }}>Changing attitudes,</span></span>
            <span className="mask-line"><span className="text-teal" style={{ animationDelay: "0.4s" }}>behaviour</span></span>
            <span className="mask-line"><span style={{ animationDelay: "0.55s" }}>and culture.</span></span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            We help complex organisations see risks as their people really see them — then change attitudes through powerful, bespoke workshops and effective performance management techniques to produce lasting habits and resilient cultures.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-enquire-cta-button"
              className="inline-flex items-center gap-2 rounded-full bg-teal px-8 py-4 text-base font-bold text-white transition-all duration-200 hover:scale-[1.04] hover:bg-navy"
            >
              Make an enquiry <ArrowRight size={18} />
            </Link>
            <Link
              to="/services"
              data-testid="hero-services-cta-button"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-8 py-4 text-base font-semibold text-navy transition-colors duration-200 hover:border-teal hover:text-teal"
            >
              Explore our services
            </Link>
          </motion.div>
          <motion.dl
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 1 }}
            className="mt-16 grid max-w-lg grid-cols-3 gap-6 border-t border-slate-200 pt-8"
          >
            {[["21+", "years in high-hazard industries"], ["75+", "countries operated in"], ["100+", "satisfied clients"]].map(([v, l]) => (
              <div key={l}>
                <dt className="font-display text-2xl sm:text-3xl font-extrabold text-teal">{v}</dt>
                <dd className="mt-1 text-xs sm:text-sm text-slate-500">{l}</dd>
              </div>
            ))}
          </motion.dl>
        </div>
      </section>

      <Marquee />

      {/* PILLARS BENTO */}
      <section className="bg-slate-50 py-20 lg:py-32" data-testid="home-services-overview">
        <div className="container-arms">
          <Reveal>
            <p className="eyebrow mb-4">What we do</p>
            <h2 className="max-w-2xl text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              Three ways we change how your organisation lives with risk
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-12">
            {SERVICES.map((s, i) => {
              return (
                <Reveal key={s.id} delay={i * 0.08} className="lg:col-span-4">
                  <Link
                    to={`/services#${s.id}`}
                    data-testid={`home-service-card-${s.id}`}
                    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200"
                  >
                    <div className="relative overflow-hidden">
                      <img src={s.image} alt={s.alt} loading="lazy" className="aspect-[16/8] w-full object-cover img-duotone transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="flex flex-1 flex-col p-8">
                      <span className="mb-4 inline-flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-white shadow-sm">
                        <img src={s.icon} alt="" loading="lazy" className="h-full w-full object-cover" />
                      </span>
                      <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-navy">{s.title}</h3>
                      <p className="mt-3 flex-1 text-sm sm:text-base text-slate-600 leading-relaxed">{s.short}</p>
                      <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-teal">
                        Learn more <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section className="bg-white py-20 lg:py-32" data-testid="home-methodology">
        <div className="container-arms">
          <Reveal>
            <p className="eyebrow mb-4">The ARMS methodology</p>
          </Reveal>
          <Reveal delay={0.1}>
            <MethodologyCogs />
          </Reveal>
          <div className="mt-6 grid gap-6 md:grid-cols-3">
            {[
              ["/assets/icon-culture-assessment.png", "Assess", "We uncover the attitudes towards risk your audits can't see — the honest baseline everything else builds on."],
              ["/assets/icon-bespoke-workshops.png", "Engage", "Actor-led drama recreates your workplace's real pressures, creating the emotional shift that slides never will."],
              ["/assets/icon-performance-management.png", "Change", "Behavioural science used to create a performance culture where new positive attitudes turn to positive habits. Driving down risk driving up performance."],
            ].map(([icon, title, text], i) => (
              <Reveal key={title} delay={i * 0.12}>
                <div className={`group relative h-full rounded-3xl border border-slate-200 bg-slate-50 p-8 lg:p-10 transition-colors duration-300 hover:border-teal hover:bg-teal-light/40 ${i === 1 ? "md:mt-11" : i === 2 ? "md:mt-[88px]" : ""}`}>
                  <span className="inline-flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-white shadow-sm">
                    <img src={icon} alt="" loading="lazy" className="h-full w-full object-cover" />
                  </span>
                  <h3 className="mt-4 text-xl sm:text-2xl font-bold tracking-tight text-navy">{title}</h3>
                  <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link to="/approach" data-testid="home-approach-link" className="mt-10 inline-flex items-center gap-2 text-base font-bold text-teal hover:text-navy transition-colors">
              See how our approach works <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FEATURED CASE STUDY */}
      <section className="bg-slate-50 py-20 lg:py-32" data-testid="home-featured-case-study">
        <div className="container-arms grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl">
              <img src={featured.image} alt={featured.title} loading="lazy" className="aspect-[4/3] w-full object-cover img-duotone" />
              <div className="absolute left-5 top-5 rounded-full bg-white/90 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-teal backdrop-blur">
                {featured.sector}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="eyebrow mb-4">Featured outcome</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">Developing leadership skills to role model and coach behaviours for excellence within their teams</h2>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">{featured.problem}</p>
            <div className="mt-8 flex items-end gap-4 rounded-2xl bg-teal-light p-6">
              <p className="font-display text-4xl sm:text-5xl font-extrabold text-teal">{featured.metric}</p>
              <p className="pb-1 text-sm text-slate-600">{featured.metricLabel}</p>
            </div>
            <Link
              to="/case-studies"
              data-testid="home-case-studies-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-teal px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-navy"
            >
              Read the case studies <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="bg-white py-20 lg:py-28" data-testid="home-testimonial">
        <div className="container-arms max-w-4xl text-center">
          <Reveal>
            <Quote className="mx-auto mb-8 text-teal" size={40} aria-hidden="true" />
            <blockquote className="text-xl sm:text-2xl lg:text-3xl font-display font-semibold leading-snug text-navy">
              "{TESTIMONIALS[0].quote}"
            </blockquote>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-teal">
              {TESTIMONIALS[0].name} — {TESTIMONIALS[0].org}{" "}
              {TESTIMONIALS[0].orgNote && <span className="text-[10px] normal-case tracking-normal text-slate-400">{TESTIMONIALS[0].orgNote}</span>}
            </p>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
